import type { FormEvent } from "react";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import MultiSelectComponent from "ui-kit/src/MultipleSelect/MultiSelectComponent";
import { _ } from "i18n";
import { DiscoverGameLinksFields } from "../../../links/DiscoverGameLinksFields";
import { createLinkEntry } from "../../../links/discoverGameLinks";
import { ProgramGameClubTeamFields } from "./ProgramGameClubTeamFields";
import { ProgramGameModalFieldError } from "./ProgramGameModalFieldError";
import {
	KICK_OFF_HOUR_OPTIONS,
	KICK_OFF_MINUTE_OPTIONS,
	ProgramGameClubTeamSide,
} from "./constants";
import type {
	OwnershipOption,
	ProgramGameCompetitionEditionOption,
	ProgramGameFormErrors,
	ProgramGameFormState,
	ProgramGameSelectOption,
} from "./types";
import { normalizeProgramGameOption } from "./formUtils";
import type { HandleSelectChange } from "ui-kit/src/MultipleSelect/types";

interface ProgramGameModalFormProps {
	form: ProgramGameFormState;
	updateForm: (
		patch: Partial<ProgramGameFormState> | ((prev: ProgramGameFormState) => ProgramGameFormState),
	) => void;
	isSubmitted: boolean;
	isSubmitting: boolean;
	isAdminUser: boolean;
	formErrors: ProgramGameFormErrors;
	homeTeamOptions: ProgramGameSelectOption[];
	awayTeamOptions: ProgramGameSelectOption[];
	homeTeamsLoading: boolean;
	awayTeamsLoading: boolean;
	homeClubHasNoTeams: boolean;
	awayClubHasNoTeams: boolean;
	loadHomeOptions: (inputValue: string) => Promise<ProgramGameSelectOption[]>;
	loadAwayOptions: (inputValue: string) => Promise<ProgramGameSelectOption[]>;
	canCreateClub: boolean;
	onRequestCreateClub: (side: ProgramGameClubTeamSide, inputValue: string) => void;
	typeOptions: ProgramGameSelectOption[];
	nationalOptions: ProgramGameSelectOption[];
	nationalLeagueOptions: ProgramGameSelectOption[];
	nationalLeaguesLoading: boolean;
	isNationalSelected: boolean;
	competitionEditionOptions: ProgramGameCompetitionEditionOption[];
	competitionEditionsLoading: boolean;
	priorityOptions: ProgramGameSelectOption[];
	typesLoading: boolean;
	ownershipItems: OwnershipOption[];
	handleOwnershipChange: HandleSelectChange;
	setTeamsheetLinkAt: (index: number, value: string) => void;
	setVideoLinkAt: (index: number, value: string) => void;
	onSubmit: (event: FormEvent) => void;
	onClose: () => void;
}

export function ProgramGameModalForm({
	form,
	updateForm,
	isSubmitted,
	isSubmitting,
	isAdminUser,
	formErrors,
	homeTeamOptions,
	awayTeamOptions,
	homeTeamsLoading,
	awayTeamsLoading,
	homeClubHasNoTeams,
	awayClubHasNoTeams,
	loadHomeOptions,
	loadAwayOptions,
	canCreateClub,
	onRequestCreateClub,
	typeOptions,
	nationalOptions,
	nationalLeagueOptions,
	nationalLeaguesLoading,
	isNationalSelected,
	competitionEditionOptions,
	competitionEditionsLoading,
	priorityOptions,
	typesLoading,
	ownershipItems,
	handleOwnershipChange,
	setTeamsheetLinkAt,
	setVideoLinkAt,
	onSubmit,
	onClose,
}: ProgramGameModalFormProps) {
	// Built at render so labels resolve after i18n translations are loaded. Values match `GenderEnum`.
	const genderOptions: ProgramGameSelectOption[] = [
		{ value: "Male", label: _("portal.gender.Male") },
		{ value: "Female", label: _("portal.gender.Female") },
		{ value: "Mixed", label: _("portal.gender.Mixed") },
	];

	return (
		<form className="discoverGamesProgramModal-form" onSubmit={(e) => void onSubmit(e)}>
			<div className="discoverGamesProgramModal-grid">
				<ProgramGameClubTeamFields
					side={ProgramGameClubTeamSide.Home}
					club={form.homeClub}
					team={form.homeTeam}
					teamOptions={homeTeamOptions}
					teamsLoading={homeTeamsLoading}
					clubHasNoTeams={homeClubHasNoTeams}
					loadClubOptions={loadHomeOptions}
					canCreateClub={canCreateClub}
					onRequestCreateClub={(inputValue) => onRequestCreateClub(ProgramGameClubTeamSide.Home, inputValue)}
					isSubmitted={isSubmitted}
					formErrors={formErrors}
					onClubChange={(club) =>
						updateForm((prev) => ({
							...prev,
							homeClub: club,
							homeTeam: null,
							nationalLeague: null,
							competitionEdition: null,
						}))
					}
					onTeamChange={(team) => updateForm({ homeTeam: team, competitionEdition: null })}
				/>

				<ProgramGameClubTeamFields
					side={ProgramGameClubTeamSide.Away}
					club={form.awayClub}
					team={form.awayTeam}
					teamOptions={awayTeamOptions}
					teamsLoading={awayTeamsLoading}
					clubHasNoTeams={awayClubHasNoTeams}
					loadClubOptions={loadAwayOptions}
					canCreateClub={canCreateClub}
					onRequestCreateClub={(inputValue) => onRequestCreateClub(ProgramGameClubTeamSide.Away, inputValue)}
					isSubmitted={isSubmitted}
					formErrors={formErrors}
					onClubChange={(club) =>
						updateForm((prev) => ({
							...prev,
							awayClub: club,
							awayTeam: null,
							nationalLeague: null,
							competitionEdition: null,
						}))
					}
					onTeamChange={(team) => updateForm({ awayTeam: team, competitionEdition: null })}
				/>

				<div className="discoverGamesProgramModal-field">
					<label className="discoverGamesProgramModal-label">
						{_("discover.gamesTable.programGame.field.type")} <span>*</span>
					</label>
					<SelectComponent
						placeholder={_("discover.gamesTable.programGame.placeholder.unfilled")}
						items={typeOptions}
						value={form.gameType}
						handleChange={(option) => updateForm({ gameType: normalizeProgramGameOption(option) })}
						isDisabled={typesLoading}
						isLoading={typesLoading}
					/>
					<ProgramGameModalFieldError show={isSubmitted} message={formErrors.gameType} />
				</div>

				<div className="discoverGamesProgramModal-field">
					<label className="discoverGamesProgramModal-label">
						{_("discover.gamesTable.programGame.field.date")} <span>*</span>
					</label>
					<input
						type="date"
						className="discoverGamesProgramModal-input"
						value={form.gameDate}
						onChange={(event) => updateForm({ gameDate: event.target.value })}
					/>
					<ProgramGameModalFieldError show={isSubmitted} message={formErrors.gameDate} />
				</div>

				<div className="discoverGamesProgramModal-field">
					<label className="discoverGamesProgramModal-label">
						{_("discover.gamesTable.programGame.field.kickOff")} <span>*</span>
					</label>
					<div className="discoverGamesProgramModal-kickoffRow">
						<SelectComponent
							placeholder={_("discover.gamesTable.programGame.placeholder.hh")}
							items={KICK_OFF_HOUR_OPTIONS}
							value={form.kickOffHour}
							handleChange={(option) => updateForm({ kickOffHour: normalizeProgramGameOption(option) })}
						/>
						<SelectComponent
							placeholder={_("discover.gamesTable.programGame.placeholder.mm")}
							items={KICK_OFF_MINUTE_OPTIONS}
							value={form.kickOffMinute}
							handleChange={(option) => updateForm({ kickOffMinute: normalizeProgramGameOption(option) })}
						/>
					</div>
					<ProgramGameModalFieldError show={isSubmitted} message={formErrors.kickOff} />
				</div>

				<div className="discoverGamesProgramModal-field">
					<label className="discoverGamesProgramModal-label">
						{_("discover.gamesTable.programGame.field.isNational")} <span>*</span>
					</label>
					<SelectComponent
						placeholder={_("discover.gamesTable.programGame.placeholder.unfilled")}
						items={nationalOptions}
						value={form.isNational}
						handleChange={(option) =>
							updateForm((prev) => ({
								...prev,
								isNational: normalizeProgramGameOption(option),
								nationalLeague: null,
							}))
						}
					/>
					<ProgramGameModalFieldError show={isSubmitted} message={formErrors.isNational} />
				</div>

				{isNationalSelected ? (
					<div className="discoverGamesProgramModal-field">
						<label className="discoverGamesProgramModal-label">
							{_("discover.gamesTable.programGame.field.nationalLeague")} <span>*</span>
						</label>
						<SelectComponent
							placeholder={_("discover.gamesTable.programGame.placeholder.unfilled")}
							items={nationalLeagueOptions}
							value={form.nationalLeague}
							handleChange={(option) => updateForm({ nationalLeague: normalizeProgramGameOption(option) })}
							isDisabled={!form.homeClub || nationalLeaguesLoading}
							isLoading={nationalLeaguesLoading}
						/>
						<ProgramGameModalFieldError show={isSubmitted} message={formErrors.nationalLeague} />
					</div>
				) : null}

				<div className="discoverGamesProgramModal-field">
					<label className="discoverGamesProgramModal-label">{_("label.user.gender")}</label>
					<SelectComponent
						placeholder={_("discover.gamesTable.programGame.placeholder.unfilled")}
						items={genderOptions}
						value={form.gender}
						isCleareable
						handleChange={(option) => updateForm({ gender: normalizeProgramGameOption(option) })}
					/>
				</div>

				<div className="discoverGamesProgramModal-field">
					<label className="discoverGamesProgramModal-label">Competition edition</label>
					<SelectComponent
						placeholder={_("discover.gamesTable.programGame.placeholder.unfilled")}
						items={competitionEditionOptions}
						value={form.competitionEdition}
						handleChange={(option) =>
							updateForm({
								competitionEdition: normalizeProgramGameOption(option),
							})
						}
						isCleareable
						isDisabled={competitionEditionsLoading}
						isLoading={competitionEditionsLoading}
						menuPortalTarget
					/>
				</div>

				{isAdminUser ? (
					<div className="discoverGamesProgramModal-field discoverGamesProgramModal-field--full">
						<label className="discoverGamesProgramModal-label">
							{_("discover.gamesTable.programGame.field.ownership")} <span>*</span>
						</label>
						<MultiSelectComponent
							placeholder={_("discover.gamesTable.programGame.placeholder.unfilled")}
							items={ownershipItems}
							value={form.ownershipSelections}
							handleChange={handleOwnershipChange}
							menuPortalTarget
							wideMenu
						/>
						<ProgramGameModalFieldError show={isSubmitted} message={formErrors.ownership} />
					</div>
				) : null}

				<div className="discoverGamesProgramModal-field discoverGamesProgramModal-field--full">
					<DiscoverGameLinksFields
						teamsheetLinks={form.teamsheetLinks}
						videoLinks={form.videoLinks}
						onTeamsheetLinkChange={setTeamsheetLinkAt}
						onVideoLinkChange={setVideoLinkAt}
						onAddTeamsheetLink={() =>
							updateForm((prev) => ({
								...prev,
								teamsheetLinks: [...prev.teamsheetLinks, createLinkEntry()],
							}))
						}
						onAddVideoLink={() =>
							updateForm((prev) => ({
								...prev,
								videoLinks: [...prev.videoLinks, createLinkEntry()],
							}))
						}
						onRemoveTeamsheetLink={(index) =>
							updateForm((prev) => ({
								...prev,
								teamsheetLinks: prev.teamsheetLinks.filter((_, i) => i !== index),
							}))
						}
						onRemoveVideoLink={(index) =>
							updateForm((prev) => ({
								...prev,
								videoLinks: prev.videoLinks.filter((_, i) => i !== index),
							}))
						}
						showRemoveIcon
					/>
				</div>

				<div className="discoverGamesProgramModal-field discoverGamesProgramModal-field--full">
					<label className="discoverGamesProgramModal-label">
						{_("discover.gamesTable.programGame.field.priority")} <span>*</span>
					</label>
					<SelectComponent
						placeholder={_("discover.gamesTable.programGame.placeholder.priority")}
						items={priorityOptions}
						value={form.priority}
						handleChange={(option) => updateForm({ priority: normalizeProgramGameOption(option) })}
					/>
					<ProgramGameModalFieldError show={isSubmitted} message={formErrors.priority} />
				</div>
			</div>

			<div className="discoverGamesProgramModal-footer">
				<button
					type="button"
					className="discoverGamesProgramModal-btnSecondary"
					onClick={onClose}
					disabled={isSubmitting}
				>
					{_("discover.gamesTable.programGame.button.cancel")}
				</button>
				<button type="submit" className="discoverGamesProgramModal-btnPrimary" disabled={isSubmitting}>
					{_("discover.gamesTable.programGame.button.enter")}
				</button>
			</div>
		</form>
	);
}
